/*
 * Sample work items (P2): tasks and follow-ups, approval requests and the
 * calendar. Every item points at a real record elsewhere in the prototype
 * (a prospect, customer, ticket…) so My Work, the task register, the
 * approvals inbox and the calendar all tell the same story.
 *
 * The signed-in user in the prototype is staff #1, Jane Wanjiku (Sales Manager).
 */
import { daysFromNow } from "@/helpers/crm";
import { prospects } from "@/mock/prospects";
import { customers } from "@/mock/customers";

export const ME = 1;

/*
 * The kinds of record a task can hang off, and the screen each one opens.
 * `param` is the route parameter the detail screen expects.
 */
export const relatedKinds = [
  { key: "prospect", name: "Prospect", route: "prospects.show", icon: "iconoir-page-search" },
  { key: "lead", name: "Lead", route: "leads.show", icon: "iconoir-user" },
  { key: "opportunity", name: "Opportunity", route: "opportunities.show", icon: "iconoir-graph-up" },
  { key: "customer", name: "Customer", route: "customers.show", icon: "iconoir-building" },
  { key: "onboarding", name: "Onboarding", route: "onboarding.show", icon: "iconoir-clipboard-check" },
  { key: "ticket", name: "Ticket", route: "support.ticket", icon: "iconoir-headset-help" },
  { key: "renewal", name: "Renewal", route: "renewals.list", icon: "iconoir-refresh-double" },
];
export const relatedKind = (key) => relatedKinds.find((k) => k.key === key);

/** The router target for a task's related record. */
export const relatedRoute = (r) => {
  const k = relatedKind(r.kind);
  if (!k) return null;
  return k.route === "renewals.list" ? { name: k.route } : { name: k.route, params: { id: r.id } };
};

const P = (id) => {
  const p = prospects.find((x) => x.id === id);
  return { kind: "prospect", id, code: p.code, label: p.organisation };
};
const C = (id) => {
  const c = customers.find((x) => x.id === id);
  return { kind: "customer", id, code: c.code, label: c.name };
};
const L = (id, label) => ({ kind: "lead", id, code: `LED-${String(40 + id).padStart(4, "0")}`, label });
const O = (id, label) => ({ kind: "opportunity", id, code: `OPP-${String(200 + id).padStart(4, "0")}`, label });
const ONB = (id, label) => ({ kind: "onboarding", id, code: `ONB-${String(30 + id).padStart(4, "0")}`, label });
const TKT = (id, label) => ({ kind: "ticket", id, code: `TKT-${String(400 + id).padStart(4, "0")}`, label });
const REN = (id, label) => ({ kind: "renewal", id, code: `REN-${String(100 + id).padStart(4, "0")}`, label });

/*
 * Tasks and follow-ups (P2.2 / P2.3). status: open | done | cancelled; a task
 * is "overdue" when it is open past its due date (derived, never stored).
 * `escalated` records the hierarchy escalation (P2.6): an overdue task goes to
 * the owner's supervisor after the delay set in Settings → Notifications.
 */
const T = (id, type, title, related, owner_id, due, time, priority, status = "open", extra = {}) => ({
  id,
  code: `TSK-${String(1200 + id).padStart(5, "0")}`,
  type,
  title,
  related,
  owner_id,
  due: daysFromNow(due),
  time,
  priority,
  status,
  created_by: extra.created_by ?? owner_id,
  created_at: daysFromNow(due - (extra.lead ?? 5)),
  source: extra.source ?? "Manual",
  escalated: extra.escalated ?? null,
  completed_at: status === "done" ? daysFromNow(Math.min(0, due)) : null,
  note: extra.note ?? "",
});

export const tasks = [
  // Jane's own queue
  T(1, "Call", "Call Dr. Otieno Oloo on budget approval", P(14), 1, -5, "10:00", "high", "open", {
    source: "Next action on prospect",
    escalated: { level: 1, to: "Brian Otieno", at: daysFromNow(-2), rule: "Overdue 3 days → supervisor" },
  }),
  T(2, "Follow-up", "Send revised proposal to Sokoni Wholesalers", P(26), 1, -1, "14:00", "medium", "open", { source: "Next action on prospect" }),
  T(3, "Meeting", "Qualification meeting – Lakeview SACCO", P(4), 1, 0, "11:30", "high", "open", { source: "Next action on prospect" }),
  T(4, "Demo", "Payroll demo for Pwani Logistics HR team", O(3, "Pwani Logistics – HR & Payroll"), 1, 0, "14:00", "high"),
  T(5, "Email", "Send revised quote QTN-0031 to Savannah Hotels", O(5, "Savannah Hotels – extra 200 employees"), 1, 0, "16:00", "medium"),
  T(6, "Call", "Discovery call – Upper Hill Medical Centre", P(2), 1, 2, "09:30", "medium", "open", { source: "Next action on prospect" }),
  T(7, "Review", "Quarterly business review – Jamii Microfinance", C(2), 1, 1, "10:00", "medium"),
  T(8, "Call", "Renewal call – Bidii Manufacturers", REN(5, "Bidii Manufacturers renewal"), 1, 3, "11:00", "high", "open", { source: "Renewal reminder (30 days)" }),
  T(9, "Follow-up", "Chase signed contract – Gatsby Insurance", O(8, "Gatsby Insurance – HR upgrade"), 1, -3, "09:00", "critical", "open", {
    escalated: { level: 2, to: "Brian Otieno", at: daysFromNow(-1), rule: "Critical task overdue 2 days → Head of Sales" },
  }),
  T(10, "Visit", "Site visit – Rift Valley Millers go-live readiness", ONB(2, "Rift Valley Millers onboarding"), 1, 5, "08:30", "medium"),
  T(11, "Follow-up", "Confirm decision maker at Kilimo Fresh", L(1, "Kilimo Fresh Exporters"), 1, 4, "15:00", "low"),
  T(12, "Email", "Share case study with Thika Steel Works", L(12, "Thika Steel Works"), 1, 6, "10:00", "low"),
  T(13, "Call", "Check in on open complaint – Savannah Hotels", TKT(14, "Payslips not emailing"), 1, -2, "12:00", "high", "open", { source: "Ticket escalation" }),
  T(14, "Meeting", "Pipeline review with SME Desk", null, 1, 1, "08:00", "medium"),
  T(15, "Call", "Introductory call – Gatsby Insurance board", O(8, "Gatsby Insurance – HR upgrade"), 1, -8, "10:00", "medium", "done"),
  T(16, "Email", "Send brochure to Mwangaza Academy", P(5), 1, -10, "09:00", "low", "done"),
  T(17, "Follow-up", "Re-engage Safari Tours after AGM", P(22), 1, -2, "09:00", "low", "cancelled", { note: "Prospect disqualified (TIM-01); recycled to pool." }),
  // the rest of the company
  T(18, "Call", "Second attempt – Kampala Retail Hub", P(10), 5, -2, "10:30", "medium", "open", {
    source: "Next action on prospect",
    escalated: { level: 1, to: "Ann Mutua", at: daysFromNow(0), rule: "Overdue 2 days → supervisor" },
  }),
  T(19, "Demo", "Self-service demo – Smart Duka shop staff", O(11, "Smart Duka – 120 more employees"), 5, 2, "14:00", "high"),
  T(20, "Call", "Qualification call – Harambee Sacco", P(20), 2, 1, "11:00", "high", "open", { source: "Next action on prospect" }),
  T(21, "Training", "Payroll admin training – Mwangaza Academy", ONB(1, "Mwangaza Academy onboarding"), 6, -1, "09:00", "high", "open", {
    escalated: { level: 1, to: "Kevin Mwangi", at: daysFromNow(0), rule: "Onboarding task overdue → Implementation lead" },
  }),
  T(22, "Meeting", "Data migration sign-off – Kampala Retail Hub", ONB(3, "Kampala Retail Hub onboarding"), 6, 3, "10:00", "medium"),
  T(23, "Review", "Health review – Coastline Freight (critical)", C(3), 7, 0, "15:00", "critical", "open", { source: "Health drop alert" }),
  T(24, "Call", "Save call – Equator Pharmacies overdue invoice", C(4), 10, -4, "11:00", "high", "open", {
    source: "Churn trigger: payment",
    escalated: { level: 2, to: "Grace Njeri", at: daysFromNow(-1), rule: "Retention task overdue 3 days → Head of CS" },
  }),
  T(25, "Follow-up", "Resolve SLA breach – Upper Hill Medical", TKT(12, "Leave balances wrong after upgrade"), 8, -2, "09:00", "critical", "open", {
    source: "SLA breach",
    escalated: { level: 1, to: "Grace Njeri", at: daysFromNow(-1), rule: "SLA breach → Head of Support" },
  }),
  T(26, "Visit", "Coast customer visits – Nyali", C(18), 4, 6, "09:00", "low"),
  T(27, "Email", "Exit checklist – data export for Tumaini NGO", C(12), 10, 2, "10:00", "medium"),
  T(28, "Call", "Renewal call – Elimu Bora Schools", REN(15, "Elimu Bora Schools renewal"), 7, 5, "10:00", "medium", "open", { source: "Renewal reminder (30 days)" }),
  T(29, "Follow-up", "Proposal follow-up – Rift Valley Millers expansion", O(14, "Rift Valley Millers – Appraisals module"), 2, -6, "15:00", "medium", "done"),
  T(30, "Call", "Win-back call – Nyali Beach Resort", C(14), 4, 8, "11:00", "low", "open", { source: "Re-engagement date reached" }),
];

/** open | done | cancelled | overdue: overdue is open and past due. */
export const taskStatus = (t) => (t.status === "open" && t.due < daysFromNow(0) ? "overdue" : t.status);
export const taskStatusTone = (s) => ({ open: "primary", done: "success", cancelled: "secondary", overdue: "danger" })[s] ?? "secondary";

/*
 * Open records the signed-in user owns that have no next action. A rule in
 * Settings → Stages & Conversion Rules says every open prospect, lead and
 * opportunity must carry one.
 */
export const recordsWithoutNextAction = [
  { ...L(7, "Westlands Dental Group"), stage: "Contacted", owner_id: 1, idle_days: 9 },
  { ...O(9, "County Health Services – HR"), stage: "Proposal", owner_id: 1, idle_days: 12 },
  { ...O(12, "Elimu Bora – Advanced upgrade"), stage: "Discovery", owner_id: 1, idle_days: 6 },
  ...prospects
    .filter((p) => p.owner_id === null && ["new", "contacted"].includes(p.stage))
    .slice(0, 2)
    .map((p) => ({ ...P(p.id), stage: "Unassigned (pool)", owner_id: null, idle_days: 0 })),
];

/*
 * Approval request types (P2.5). The chain for each comes from the approval
 * matrix (Configuration → Approval Matrix); thresholds are tenant settings.
 * NEW LIST: belongs to the approval matrix settings.
 */
export const approvalTypes = [
  { key: "discount", name: "Discount above threshold", module: "Quotations", rule: "Discount > 10% of list price", icon: "iconoir-percentage" },
  { key: "proposal", name: "Proposal approval", module: "Opportunities", rule: "Every proposal over KES 1M", icon: "iconoir-page-star" },
  { key: "reassign", name: "Prospect mass reassignment", module: "Prospects", rule: "Moving prospects between owners", icon: "iconoir-repeat" },
  { key: "exit", name: "Exit approval", module: "Retention", rule: "Every customer exit", icon: "iconoir-warning-triangle" },
  { key: "temp_access", name: "Temporary access", module: "Security", rule: "Any temporary grant", icon: "iconoir-key" },
  { key: "role_change", name: "Role / permission change", module: "Security", rule: "Any change to a role or user's roles", icon: "iconoir-shield-check" },
  { key: "onboarding_close", name: "Onboarding close with accepted critical issues", module: "Onboarding", rule: "Closing while critical issues are open", icon: "iconoir-clipboard-check" },
  { key: "config_change", name: "Configuration change", module: "Settings", rule: "Changes to stages, reason codes, KPIs, SLA", icon: "iconoir-settings" },
];
export const approvalType = (key) => approvalTypes.find((t) => t.key === key);

// step: { role, approver_id, status: approved | pending | waiting | rejected | returned, at?, comment? }
const S = (role, approver_id, status, at = null, comment = "") => ({ role, approver_id, status, at: at === null ? null : daysFromNow(at), comment });

const A = (id, type, requester_id, submitted, summary, record, amount, chain, sla_hours, extra = {}) => {
  const current = chain.findIndex((s) => s.status === "pending");
  const status = extra.status ?? (current >= 0 ? "pending" : "approved");
  return {
    id,
    code: `APR-${String(500 + id).padStart(5, "0")}`,
    type,
    requester_id,
    submitted_at: `${daysFromNow(-submitted)}T${extra.time ?? "09:20"}:00`,
    summary,
    record,
    amount,
    detail: extra.detail ?? [],
    chain,
    current_step: current >= 0 ? current + 1 : chain.length,
    sla_hours,
    // hours already spent at the current step
    waiting_hours: extra.waiting_hours ?? submitted * 8,
    status,
  };
};

export const approvals = [
  A(1, "discount", 5, 1, "18% discount on Smart Duka expansion (+120 employees)", O(11, "Smart Duka – 120 more employees"), 936000,
    [S("Sales Manager", 1, "pending")], 24, {
      waiting_hours: 20,
      detail: [["List price", "KES 1,141,463"], ["Discount", "18% (limit for Sales: 10%)"], ["Net value", "KES 936,000"], ["Competitor", "Zoho People quoted 15% lower"]],
    }),
  A(2, "proposal", 3, 0, "Proposal v2 – Kilimo Fresh Exporters HR & Payroll, 3-year term", O(2, "Kilimo Fresh – HR & Payroll"), 2160000,
    [S("Sales Manager", 1, "pending"), S("Finance", 11, "waiting")], 48, {
      waiting_hours: 5,
      detail: [["Package", "DeeHr360 Standard, 200 users"], ["Term", "36 months"], ["Payment", "Quarterly in advance"], ["Margin", "41% (target 35%)"]],
    }),
  A(3, "reassign", 4, 2, "Move 6 prospects from Tom Barasa (exited) to Peter Ouma", null, null,
    [S("Sales Manager", 1, "pending")], 24, {
      waiting_hours: 30,
      detail: [["Prospects", "PRS-0103, PRS-0111, PRS-0118, PRS-0121, PRS-0124, PRS-0128"], ["Reason", "Owner exited the company"]],
    }),
  A(4, "exit", 10, 3, "Exit request – Tumaini NGO (DeeHr360 HRM)", C(12), 216000,
    [S("Customer Success Manager", 7, "approved", -2, "Save attempts exhausted; exit reason confirmed."), S("Sales Manager", 1, "pending"), S("Finance", 11, "waiting")], 48, {
      waiting_hours: 40,
      detail: [["Exit reason", "BUS-02 · Merged / acquired"], ["Annual value lost", "KES 216,000"], ["Outstanding invoices", "None"], ["Data export", "Requested"]],
    }),
  A(5, "onboarding_close", 6, 1, "Close Mwangaza Academy onboarding with 1 accepted critical issue", ONB(1, "Mwangaza Academy onboarding"), null,
    [S("Implementation Lead", 6, "approved", -1, "Workaround in place."), S("Sales Manager", 1, "pending"), S("Head of CS", 7, "waiting")], 24, {
      waiting_hours: 9,
      detail: [["Critical issue", "Bank file format for KCB not yet certified"], ["Workaround", "Manual upload until certification (est. 2 weeks)"], ["Customer sign-off", "Received, issue accepted in writing"]],
    }),
  A(6, "discount", 1, 1, "12% discount for Gatsby Insurance multi-year renewal", O(8, "Gatsby Insurance – HR upgrade"), 823680,
    [S("Sales Manager", 1, "pending"), S("Head of Sales", 2, "waiting")], 24, {
      waiting_hours: 6,
      detail: [["Note", "You raised this request, so you cannot approve it. It routes to the next approver."], ["Net value", "KES 823,680"]],
    }),
  A(7, "temp_access", 8, 0, "Temporary view of contract values for Faith Achieng (5 days)", null, null,
    [S("Head of Sales", 2, "pending"), S("CTO / Admin", 6, "waiting")], 8, {
      waiting_hours: 3,
      detail: [["Access", "Field: contract value, scope Department"], ["From – to", `${daysFromNow(0)} → ${daysFromNow(5)} (auto-expires)`], ["Reason", "Reconciling credit notes for SLA penalties"]],
    }),
  A(8, "role_change", 2, 1, "Grant Lucy Chebet 'Sales Manager' role (acting)", null, null,
    [S("CTO / Admin", 6, "pending"), S("Audit / Risk", 11, "waiting")], 24, { waiting_hours: 26 }),
  A(9, "config_change", 9, 4, "Change lead score threshold for 'Hot' from 70 to 65", null, null,
    [S("Head of Sales", 2, "approved", -3, "Fine – review in 30 days."), S("CTO / Admin", 6, "pending")], 48, { waiting_hours: 60 }),
  // Jane's own requests
  A(10, "proposal", 1, 2, "Proposal v1 – County Health Services HR (tender)", O(9, "County Health Services – HR"), 4800000,
    [S("Head of Sales", 2, "approved", -1, "OK on pricing."), S("Finance", 11, "pending")], 48, { waiting_hours: 22 }),
  A(11, "reassign", 1, 6, "Move 4 Coast prospects from Ann Mutua to Peter Ouma", null, null,
    [S("Head of Sales", 2, "approved", -5, "Agreed – territory alignment.")], 24, { status: "approved" }),
  A(12, "discount", 1, 9, "25% discount for Lakeview SACCO first-year", O(4, "Lakeview SACCO – Demulla LMS"), 810000,
    [S("Head of Sales", 2, "rejected", -8, "25% is beyond policy. Offer 12% with a 2-year term instead.")], 24, { status: "rejected" }),
  A(13, "config_change", 1, 3, "Add reason code PRC-03 'Payment terms not acceptable'", null, null,
    [S("Head of Sales", 2, "returned", -2, "Please say which points it applies to and whether it is preventable.")], 48, { status: "returned" }),
  // decided by others, visible in All
  A(14, "exit", 7, 30, "Exit – Nyali Beach Resort (HR & Payroll)", C(14), 540000,
    [S("Customer Success Manager", 10, "approved", -29), S("Sales Manager", 1, "approved", -28, "Competitor offer could not be matched."), S("Finance", 11, "approved", -27)], 48, { status: "approved" }),
];

/*
 * Calendar (demos, meetings, visits, renewals, go-lives). Colours per type are
 * the theme tones; demos and visits come from the demo and engagement
 * screens, renewals and go-lives from the contract and onboarding dates.
 */
export const eventTypes = [
  { key: "demo", name: "Demo", color: "#7367f0" },
  { key: "meeting", name: "Meeting", color: "#0d6efd" },
  { key: "visit", name: "Visit", color: "#20c997" },
  { key: "call", name: "Call", color: "#6c757d" },
  { key: "renewal", name: "Renewal", color: "#f9b931" },
  { key: "golive", name: "Go-live", color: "#22c55e" },
  { key: "training", name: "Training", color: "#0dcaf0" },
];
export const eventType = (key) => eventTypes.find((t) => t.key === key);

const E = (id, type, title, day, start, end, owner_id, related, location = "") => ({
  id,
  type,
  title,
  start: start ? `${daysFromNow(day)}T${start}:00` : daysFromNow(day),
  end: end ? `${daysFromNow(day)}T${end}:00` : null,
  all_day: !start,
  owner_id,
  related,
  location,
});

export const calendarEvents = [
  E(1, "meeting", "Qualification meeting – Lakeview SACCO", 0, "11:30", "12:30", 1, P(4), "Kisumu (Teams)"),
  E(2, "demo", "Payroll demo – Pwani Logistics", 0, "14:00", "15:30", 1, O(3, "Pwani Logistics – HR & Payroll"), "Client office, Nyali"),
  E(3, "call", "Renewal call – Bidii Manufacturers", 3, "11:00", "11:30", 1, REN(5, "Bidii Manufacturers renewal")),
  E(4, "review", "QBR – Jamii Microfinance", 1, "10:00", "11:30", 1, C(2), "Upper Hill"),
  E(5, "visit", "Go-live readiness visit – Rift Valley Millers", 5, "08:30", "12:00", 1, ONB(2, "Rift Valley Millers onboarding"), "Nakuru"),
  E(6, "demo", "Self-service demo – Smart Duka", 2, "14:00", "15:00", 5, O(11, "Smart Duka – 120 more employees"), "Westlands"),
  E(7, "training", "Payroll admin training – Mwangaza Academy", -1, "09:00", "13:00", 6, ONB(1, "Mwangaza Academy onboarding"), "Thika"),
  E(8, "golive", "Go-live: Kampala Retail Hub", 9, null, null, 6, ONB(3, "Kampala Retail Hub onboarding")),
  E(9, "golive", "Go-live: Rift Valley Millers", 14, null, null, 6, ONB(2, "Rift Valley Millers onboarding")),
  E(10, "renewal", "Renewal due: Savannah Hotels Group", 30, null, null, 1, C(1)),
  E(11, "renewal", "Renewal due: Bidii Manufacturers", 30, null, null, 1, C(5)),
  E(12, "renewal", "Renewal due: Tumaini NGO", 20, null, null, 10, C(12)),
  E(13, "renewal", "Renewal due: Elimu Bora Schools", 35, null, null, 7, C(15)),
  E(14, "meeting", "Pipeline review – SME Desk", 1, "08:00", "09:00", 1, null, "Board room"),
  E(15, "visit", "Health visit – Coastline Freight", 0, "15:00", "16:30", 7, C(3), "Mombasa"),
  E(16, "demo", "Appraisals demo – Rift Valley Millers", 7, "10:00", "11:30", 2, O(14, "Rift Valley Millers – Appraisals module"), "Teams"),
  E(17, "meeting", "Executive sponsor call – Harambee Sacco", 4, "16:00", "17:00", 2, C(9), "Teams"),
  E(18, "visit", "Coast customer visits", 6, "09:00", "17:00", 4, C(18), "Mombasa"),
  E(19, "demo", "LMS mobile-loans demo – Harambee Sacco branches", -3, "10:00", "12:00", 2, P(20), "Upper Hill"),
  E(20, "training", "Train-the-trainer – Kampala Retail Hub", 8, "09:00", "16:00", 6, ONB(3, "Kampala Retail Hub onboarding"), "Kampala"),
  E(21, "call", "Win-back call – Nyali Beach Resort", 8, "11:00", "11:30", 4, C(14)),
  E(22, "meeting", "Monthly sales & CS forecast", 10, "09:00", "11:00", 2, null, "Board room"),
];
// QBRs are shown as meetings.
calendarEvents.forEach((e) => {
  if (e.type === "review") e.type = "meeting";
});
